import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import reelProjectServiceInstance from '../../services/ReelProjectService';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const platformPresetLabels = {
    horizontal: 'Horizontal (16:9)',
    vertical: 'Vertical (9:16)',
    square: 'Square (1:1)',
};

const STATUS = {
    IDLE: 'idle',
    PROCESSING: 'processing',
    DONE: 'done',
    FAILED: 'failed',
};

// How many projects export concurrently. Keeps the dialog responsive for large
// batches without firing every job at once and flooding the backend export queue.
const MAX_CONCURRENT_EXPORTS = 3;

const BulkReelExportDialog = ({ open, onClose, projects = [], buildExportPayload }) => {
    useTrackUsage('BulkReelExportDialog', 'dialog', 'reel-editor', open);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [statusById, setStatusById] = useState({});
    const [urlById, setUrlById] = useState({});
    const [errorById, setErrorById] = useState({});

    useEffect(() => {
        if (open) {
            setSelectedIds(new Set(projects.map((p) => p.id)));
            setSelectAll(true);
            setStatusById({});
            setUrlById({});
            setErrorById({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, projects]);

    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        setSelectedIds(checked ? new Set(projects.map((p) => p.id)) : new Set());
    };

    const handleToggle = (projectId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(projectId)) next.delete(projectId);
            else next.add(projectId);
            setSelectAll(next.size === projects.length);
            return next;
        });
    };

    const pollExportStatus = async (jobId) => {
        const maxPollCount = 720; // ~60 minutes at 5s interval — large videos can take a while to render
        for (let pollCount = 0; pollCount < maxPollCount; pollCount += 1) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, 5000));
            // eslint-disable-next-line no-await-in-loop
            const statusResponse = await reelProjectServiceInstance.getExportStatus(jobId);
            const status = statusResponse?.status || 'PROCESSING';
            const readyUrl = statusResponse?.downloadUrl || statusResponse?.azureBlobUrl || '';

            if (status === 'COMPLETED') {
                return { ok: true, url: readyUrl };
            }
            if (status === 'FAILED') {
                return { ok: false, message: statusResponse?.message || 'Video export failed.' };
            }
        }
        return { ok: false, message: 'Export is still processing. Please check back later.' };
    };

    const handleExport = async () => {
        const targets = projects.filter((p) => selectedIds.has(p.id));
        if (targets.length === 0) return;

        setExporting(true);
        const nextStatus = {};
        targets.forEach((p) => { nextStatus[p.id] = STATUS.PROCESSING; });
        setStatusById(nextStatus);
        setUrlById({});
        setErrorById({});

        const exportOne = async (project) => {
            try {
                const payload = buildExportPayload(project);
                if (!payload) {
                    throw new Error('No subtitle cues available to export.');
                }

                const exportResponse = await reelProjectServiceInstance.exportVideo(payload);
                const initialUrl = exportResponse?.downloadUrl || exportResponse?.azureBlobUrl || '';
                const jobId = exportResponse?.jobId || '';

                let result;
                if (initialUrl) {
                    result = { ok: true, url: initialUrl };
                } else if (jobId) {
                    result = await pollExportStatus(jobId);
                } else {
                    result = { ok: false, message: 'Export request did not return a job ID.' };
                }

                if (result.ok) {
                    setStatusById((prev) => ({ ...prev, [project.id]: STATUS.DONE }));
                    setUrlById((prev) => ({ ...prev, [project.id]: result.url }));
                } else {
                    setStatusById((prev) => ({ ...prev, [project.id]: STATUS.FAILED }));
                    setErrorById((prev) => ({ ...prev, [project.id]: result.message }));
                }
                return result.ok;
            } catch (error) {
                console.error(`Failed to export reel project ${project.id}:`, error);
                setStatusById((prev) => ({ ...prev, [project.id]: STATUS.FAILED }));
                setErrorById((prev) => ({
                    ...prev,
                    [project.id]: error?.response?.data?.message || error?.message || 'Export failed.',
                }));
                return false;
            }
        };

        // Bounded worker pool: up to MAX_CONCURRENT_EXPORTS projects export at once,
        // each worker picking up the next queued project as soon as it finishes,
        // instead of exporting strictly one-at-a-time.
        let nextIndex = 0;
        let successCount = 0;
        const runWorker = async () => {
            while (nextIndex < targets.length) {
                const project = targets[nextIndex];
                nextIndex += 1;
                // eslint-disable-next-line no-await-in-loop
                const ok = await exportOne(project);
                if (ok) successCount += 1;
            }
        };
        const workerCount = Math.min(MAX_CONCURRENT_EXPORTS, targets.length);
        await Promise.all(Array.from({ length: workerCount }, runWorker));

        setExporting(false);
        void Swal.fire({
            icon: successCount === targets.length ? 'success' : 'warning',
            title: 'Bulk Export Complete',
            text: `${successCount} of ${targets.length} project${targets.length !== 1 ? 's' : ''} exported successfully.`,
            toast: true,
            position: 'top-end',
            timer: 4000,
            timerProgressBar: true,
            showConfirmButton: false,
        });
    };

    const selectedCount = selectedIds.size;

    const renderStatusIcon = (projectId) => {
        const status = statusById[projectId];
        if (status === STATUS.PROCESSING) return <CircularProgress size={18} />;
        if (status === STATUS.DONE) return <CheckCircleIcon color="success" fontSize="small" />;
        if (status === STATUS.FAILED) return <ErrorOutlineIcon color="error" fontSize="small" />;
        return null;
    };

    return (
        <Dialog open={open} onClose={!exporting ? onClose : undefined} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MovieFilterIcon color="primary" />
                    <Typography variant="h6">Bulk Export Reel Projects</Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                {projects.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">No reel projects to export.</Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selectAll}
                                        indeterminate={selectedCount > 0 && selectedCount < projects.length}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        icon={<CheckBoxOutlineBlankIcon />}
                                        checkedIcon={<CheckBoxIcon />}
                                        disabled={exporting}
                                    />
                                }
                                label={`Select All (${projects.length} projects)`}
                            />
                            {selectedCount > 0 && (
                                <Chip label={`${selectedCount} selected`} color="primary" size="small" />
                            )}
                        </Box>

                        <Divider sx={{ mb: 1.5 }} />

                        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {projects.map((project) => {
                                const isSelected = selectedIds.has(project.id);
                                const formatLabel = platformPresetLabels[project.reelFormat] || project.reelFormat;
                                const downloadUrl = urlById[project.id];
                                const errorMessage = errorById[project.id];

                                return (
                                    <ListItem
                                        key={project.id}
                                        disablePadding
                                        secondaryAction={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {renderStatusIcon(project.id)}
                                                {downloadUrl && (
                                                    <IconButton
                                                        size="small"
                                                        component="a"
                                                        href={downloadUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label={`Download ${project.projectName || project.id}`}
                                                    >
                                                        <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        }
                                    >
                                        <ListItemButton
                                            onClick={() => handleToggle(project.id)}
                                            disabled={exporting}
                                        >
                                            <ListItemIcon>
                                                <Checkbox
                                                    checked={isSelected}
                                                    tabIndex={-1}
                                                    disableRipple
                                                    icon={<CheckBoxOutlineBlankIcon />}
                                                    checkedIcon={<CheckBoxIcon />}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={project.projectName || `${project.languageName} — ${formatLabel}`}
                                                secondary={
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                        <Chip label={project.languageName} size="small" variant="outlined" />
                                                        <Chip label={formatLabel} size="small" variant="outlined" />
                                                    </Box>
                                                }
                                                secondaryTypographyProps={{ component: 'div' }}
                                            />
                                        </ListItemButton>
                                        {errorMessage && (
                                            <Alert severity="error" sx={{ ml: 2, mr: 2, mb: 1 }}>
                                                {errorMessage}
                                            </Alert>
                                        )}
                                    </ListItem>
                                );
                            })}
                        </List>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={exporting}>
                    {exporting ? 'Close' : 'Cancel'}
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={exporting ? <CircularProgress size={20} /> : <MovieFilterIcon />}
                    onClick={handleExport}
                    disabled={selectedCount === 0 || exporting}
                >
                    {exporting ? 'Exporting...' : `Export ${selectedCount} Project${selectedCount !== 1 ? 's' : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkReelExportDialog;
